import { AudioTrackRow } from "@/components/audioTracks/AudioTrackRow";
import { Skeleton } from "@/components/shared/Skeleton";
import type { ReorderInfo } from "@/lib/dnd/components/Sortable";
import { Sortable } from "@/lib/dnd/components/Sortable";
import {
  useAddAudioTracks,
  useDeleteAudioTracks,
  useRenameAudioTrack,
  useReorderAudioTrack,
} from "@/lib/hooks/audioTracks/useAudioTrackMutations";
import { useAudioTracks } from "@/lib/hooks/audioTracks/useAudioTracks";
import { LoadMoreButton } from "@ai-teach/ui-web";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { type MutableRefObject, useRef, useState } from "react";
import { MdHeadphones, MdUploadFile } from "react-icons/md";

interface AudioFolderViewProps {
  folderId: string;
  /**
   * Filled in with a function that opens the file picker, so the page header's "+" button
   * can trigger an upload without duplicating the input element.
   */
  openUploadRef?: MutableRefObject<(() => void) | null>;
}

/**
 * The contents of an audio folder: upload, reorder, rename and delete its audio files.
 *
 * Rendered by ProjectDetailPage in place of the Projects/Lessons sections when the
 * project is an audio folder. It owns no routing — an audio folder is still a project and
 * still lives at /admin/project/$projectId, so breadcrumbs, trash and move keep working
 * exactly as they do for any other project.
 */
export function AudioFolderView({ folderId, openUploadRef }: AudioFolderViewProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);

  if (openUploadRef) {
    openUploadRef.current = () => fileInputRef.current?.click();
  }

  const { tracks, hasMore, loading, error, loadMore } = useAudioTracks(folderId);
  const { addAudioTracks, loading: uploading } = useAddAudioTracks();
  const { renameAudioTrack } = useRenameAudioTrack();
  const { deleteAudioTracks, loading: deleting } = useDeleteAudioTracks();
  const { reorderAudioTrack } = useReorderAudioTrack();

  const handleFiles = async (fileList: FileList | null) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;

    try {
      await addAudioTracks(folderId, files);
    } catch {
      // withToast already surfaced the failure, including which file stopped the batch.
    } finally {
      // Clearing lets the same file be picked again after a failed upload.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleReorder = (newTracks: typeof tracks, { newIndex }: ReorderInfo) => {
    const moved = newTracks[newIndex];
    if (!moved) return;

    const previous = newTracks[newIndex - 1];
    const next = newTracks[newIndex + 1];
    const prevSortOrder = previous?.sortOrder ?? null;
    const newSortOrder = next != null ? ((prevSortOrder ?? 0) + (next.sortOrder ?? 0)) / 2 : null;

    return reorderAudioTrack(moved.id, prevSortOrder, newSortOrder);
  };

  return (
    <>
      <div className="flex items-center gap-2 mb-6">
        <MdHeadphones className="w-5 h-5 text-secondary-200" aria-hidden="true" />
        <h2 className="h4">Audio Files</h2>
        <span className="badge-secondary">{tracks.length}</span>
      </div>

      {/* Upload area */}
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDraggingFiles(true);
        }}
        onDragLeave={() => setIsDraggingFiles(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDraggingFiles(false);
          handleFiles(e.dataTransfer.files);
        }}
        disabled={uploading}
        className={`w-full max-w-4xl mb-8 rounded-xl border-2 p-8 flex flex-col items-center justify-center gap-2 transition-colors ${
          isDraggingFiles
            ? "border-primary-500 bg-primary-500/10"
            : "border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 hover:border-primary-500"
        } ${uploading ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
      >
        <MdUploadFile className="w-10 h-10 text-secondary-400" aria-hidden="true" />
        <span className="label">
          {uploading ? "Uploading..." : "Drop audio files here, or click to choose"}
        </span>
        <span className="caption">MP3, M4A, WAV, OGG · up to 50MB each</span>
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {/* Track list */}
      {loading && tracks.length === 0 ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={`audio-track-skeleton-${i + 1}`} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-error-500">Failed to load audio files.</p>
          <p className="caption mt-1">{error.message}</p>
        </div>
      ) : tracks.length === 0 ? (
        <div className="text-center py-12">
          <MdHeadphones className="w-12 h-12 mx-auto text-gray-400" aria-hidden="true" />
          <p className="label mt-3">No audio files yet</p>
          <p className="caption mt-1">
            Upload files above. Students see this folder once it has at least one file.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 max-w-4xl">
          <Sortable items={tracks} onReorder={handleReorder} strategy={verticalListSortingStrategy}>
            {(track, index) => (
              <AudioTrackRow
                track={track}
                index={index}
                onRename={renameAudioTrack}
                onDelete={(id) => deleteAudioTracks([id])}
                disabled={deleting}
              />
            )}
          </Sortable>
        </div>
      )}

      {tracks.length > 0 && (
        <div className="max-w-4xl">
          <LoadMoreButton onClick={loadMore} hasMore={hasMore} loading={loading} />
        </div>
      )}
    </>
  );
}
